'use client'

import Lightbox from 'app/_components/Lightbox'
import { cx } from 'lib/cx'
import {
  type RenderElementProps,
  useFocused,
  useReadOnly,
  useSelected,
} from 'slate-react'

const resolutions = [400, 800, 1200, 1600, 2400]
const aspect = {
  min: 14 / 16,
  max: 16 / 9,
}

function privateImageLoader({
  src,
  width,
  quality,
}: {
  src: string
  width: number
  quality?: number
}) {
  const params = new URLSearchParams()
  const targetResolution = resolutions.find((resolution) => resolution >= width)

  if (targetResolution) params.set('w', targetResolution.toString())
  if (quality) params.set('q', quality.toString())

  if (!params.size) return src

  return `${src}?${params.toString()}`
}

export function getClampedAspectRatio(width?: number, height?: number) {
  return Math.min(
    Math.max((width || 16) / (height || 9), aspect.min),
    aspect.max,
  )
}

export default function Image({
  attributes,
  children,
  element,
}: RenderElementProps) {
  if (element.type !== 'image' && element.type !== 'private-image')
    throw new Error('Unsupported file type')

  const isEditable = !useReadOnly()
  const isSelected = useSelected()
  const isFocused = useFocused()
  const url =
    element.type === 'image'
      ? element.url
      : `/api/files/${encodeURI(element.key)}`

  const clampedAspectRatio = getClampedAspectRatio(
    element.width,
    element.height,
  )

  return (
    <div {...attributes} className="relative mb-4" contentEditable={false}>
      <Lightbox
        url={url}
        className={cx(
          'not-prose',
          isEditable && isSelected && isFocused && 'ring-text-primary ring-2',
          isEditable && 'mb-0',
        )}
        style={{
          aspectRatio: clampedAspectRatio,
        }}
        disabled={isEditable}
        nextImageProps={{
          className: cx(isEditable && 'bg-gray-20'),
          blurDataURL: element.thumbnailDataUrl,
          placeholder: element.thumbnailDataUrl ? 'blur' : 'empty',
          alt: url,
          loader: element.type === 'image' ? undefined : privateImageLoader,
        }}
      >
        {children}
      </Lightbox>
    </div>
  )
}
